import * as THREE from 'three';

export class LightingSystem {
  private scene: THREE.Scene;
  
  // Core lights
  public sunLight!: THREE.DirectionalLight;
  public ambientLight!: THREE.AmbientLight;
  
  // Dynamic lights
  public engineGlows: Map<string, THREE.PointLight> = new Map();
  public explosionFlashes: Set<THREE.PointLight> = new Set();
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.setupCoreLights();
  }
  
  private setupCoreLights(): void {
    // Sun light - bright directional light from center
    this.sunLight = new THREE.DirectionalLight(0xffd700, 2.0);
    this.sunLight.position.set(0, 0, 0);
    this.sunLight.target.position.set(50, 0, 0);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 2048;
    this.sunLight.shadow.mapSize.height = 2048;
    this.sunLight.shadow.camera.near = 0.5;
    this.sunLight.shadow.camera.far = 500;
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);
    
    // Ambient light - subtle fill lighting
    this.ambientLight = new THREE.AmbientLight(0x404080, 0.3);
    this.scene.add(this.ambientLight);
  }
  
  setSunIntensity(intensity: number): void {
    this.sunLight.intensity = intensity;
  }
  
  setAmbientIntensity(intensity: number): void {
    this.ambientLight.intensity = intensity;
  }
  
  addEngineGlow(shipId: string, color: number, intensity: number, position: THREE.Vector3): void {
    // Remove existing glow if present
    this.removeEngineGlow(shipId);
    
    const light = new THREE.PointLight(color, intensity, 10);
    light.position.copy(position);
    light.userData = { shipId, type: 'engineGlow' };
    
    this.engineGlows.set(shipId, light);
    this.scene.add(light);
  }
  
  removeEngineGlow(shipId: string): void {
    const light = this.engineGlows.get(shipId);
    if (light) {
      this.scene.remove(light);
      this.engineGlows.delete(shipId);
    }
  }
  
  updateEngineGlow(shipId: string, position: THREE.Vector3, intensity?: number): void {
    const light = this.engineGlows.get(shipId);
    if (light) {
      light.position.copy(position);
      if (intensity !== undefined) {
        light.intensity = intensity;
      }
    }
  }
  
  flash(position: THREE.Vector3, color: number, duration: number, intensity: number = 5.0): void {
    const light = new THREE.PointLight(color, intensity, 50);
    light.position.copy(position);
    light.userData = { 
      type: 'flash', 
      startTime: Date.now(), 
      duration,
      originalIntensity: intensity 
    };
    
    this.explosionFlashes.add(light);
    this.scene.add(light);
    
    // Auto-remove after duration
    setTimeout(() => {
      this.scene.remove(light);
      this.explosionFlashes.delete(light);
    }, duration);
  }
  
  update(_deltaTime: number): void {
    // Update dynamic effects
    const now = Date.now();
    
    // Update explosion flashes
    for (const light of this.explosionFlashes) {
      const { startTime, duration } = light.userData;
      const elapsed = now - startTime;
      const progress = elapsed / duration;
      
      if (progress >= 1.0) {
        this.scene.remove(light);
        this.explosionFlashes.delete(light);
      } else {
        // Fade out over time
        const fadeProgress = 1.0 - progress;
        light.intensity = light.userData['originalIntensity'] * fadeProgress;
      }
    }
  }
  
  dispose(): void {
    // Clean up all lights
    this.scene.remove(this.sunLight);
    this.scene.remove(this.sunLight.target);
    this.scene.remove(this.ambientLight);
    
    for (const light of this.engineGlows.values()) {
      this.scene.remove(light);
    }
    this.engineGlows.clear();
    
    for (const light of this.explosionFlashes) {
      this.scene.remove(light);
    }
    this.explosionFlashes.clear();
  }
  
  exportState() {
    return {
      sunLight: {
        intensity: this.sunLight.intensity,
        position: this.sunLight.position.toArray(),
        color: this.sunLight.color.getHex(),
      },
      ambientLight: {
        intensity: this.ambientLight.intensity,
        color: this.ambientLight.color.getHex(),
      },
      engineGlows: Array.from(this.engineGlows.entries()).map(([id, light]) => ({
        id,
        intensity: light.intensity,
        position: light.position.toArray(),
        color: light.color.getHex(),
      })),
      explosionFlashes: Array.from(this.explosionFlashes).map(light => ({
        intensity: light.intensity,
        position: light.position.toArray(),
        color: light.color.getHex(),
      })),
    };
  }
}